<template>
  <div class="ai-chatbot-iframe-container">
    <iframe
      v-if="!hasError"
      ref="iframe"
      :src="src"
      @load="onLoad"
      @error="onError"
    ></iframe>

    <component
      v-if="hasError"
      :is="errorComponent"
      @refresh="refresh"
      @closePanel="closePanel"
    />
  </div>
</template>

<script>
import * as Sentry from '@sentry/vue'
import utils from '@/util'

const CHATBOT_MESSAGE_TYPE_CLOSING = 'CHATBOT_CLOSING'
const CHATBOT_MESSAGE_SOURCE_AHA = 'aha-chatbot'

export default {
  props: {
    src: {
      type: String,
      required: true,
    },
    errorComponent: {
      type: [Object, String],
      default: null,
    },
  },

  data() {
    return {
      hasError: false,
      loadTimeoutAbortController: null,
      handlePostMessage: null,
    }
  },

  mounted() {
    // Check for immediate offline state
    if (!navigator.onLine) {
      this.onError()
      return
    }

    // Start timeout without blocking mounting
    this.startLoadTimeout()

    // Add postMessage listener for iframe communication
    this.setupPostMessageListener()
  },

  beforeDestroy() {
    this.clearLoadTimeout()
    this.removePostMessageListener()
  },

  methods: {
    setupPostMessageListener() {
      // Listen for postMessage events from the iframe
      this.handlePostMessage = (event) => {
        let expectedOrigin
        try {
          // Ensure this.src is a valid URL before attempting to parse its origin.
          // The 'src' prop is required, so it should generally be valid.
          expectedOrigin = new URL(this.src).origin
        } catch (e) {
          Sentry.captureException(e)
          // If the src is invalid, we cannot determine the expected origin, so ignore the message.
          return
        }

        if (event.origin !== expectedOrigin) {
          // Message from an unexpected source, ignore it.
          Sentry.captureException(new Error(`AIChatbotIframe: Received message from unexpected origin: ${event.origin}. Expected: ${expectedOrigin}`))
          return
        }

        // Get the message data
        const { data } = event
        if (!data || typeof data !== 'object') return

        // Handle chatbot closing request
        if (data.type === CHATBOT_MESSAGE_TYPE_CLOSING && data.source === CHATBOT_MESSAGE_SOURCE_AHA) {
          // Call closePanel which will emit to AIAutoPanel to call setRightNavActiveTabAction('')
          this.closePanel()
        }
      }

      window.addEventListener('message', this.handlePostMessage)
    },

    removePostMessageListener() {
      if (this.handlePostMessage) {
        window.removeEventListener('message', this.handlePostMessage)
        this.handlePostMessage = null
      }
    },

    async startLoadTimeout() {
      this.loadTimeoutAbortController = new AbortController()
      try {
        await utils.sleep(5000) // 5 second timeout
        if (!this.loadTimeoutAbortController.signal.aborted && !this.hasError) {
          this.onError()
        }
      } catch (error) {
        // Timeout was aborted, ignore
      }
    },

    clearLoadTimeout() {
      if (this.loadTimeoutAbortController) {
        this.loadTimeoutAbortController.abort()
        this.loadTimeoutAbortController = null
      }
    },

    onLoad() {
      // Clear timeout on successful load
      this.clearLoadTimeout()
      this.hasError = false
    },

    onError() {
      // Clear timeout on error
      this.clearLoadTimeout()
      this.hasError = true
    },

    refresh() {
      // Check for offline state before refresh
      if (!navigator.onLine) {
        this.hasError = true
        return
      }

      this.hasError = false
      // Force iframe reload by updating the src
      if (this.$refs.iframe) {
        this.$refs.iframe.src = this.src
      }

      // Reset timeout for the refresh attempt
      this.startLoadTimeout()
    },

    closePanel() {
      this.$emit('closePanel')
    },
  },
}
</script>

<style scoped>
.ai-chatbot-iframe-container {
  width: 100%;
  height: 100%;
}

iframe {
  width: 100%;
  height: 100%;
  border: none;
}
</style>
